// This is the client's API it uses for interacting with the `server`.

import type { CoreUserRoleType, PageType, SectionMonitoringData } from "../components/server-types";

import { SERVER_API_PATH } from "./api-constants";
import type {
  InstructorAssessmentDataForSectionType,
  InviteStatus,
  PageAssessmentStatusType,
  ProgressOverviewData,
  SectionAssessmentSummaryParams,
  StudentAssessmentDataForSectionType,
  SystemCheckResultsData,
  UserEngagementDataType,
  UserInviteStatusType,
} from "./api-data-types";
import { reportApiError, serverApi } from "./api-server-actions";
import { createCached } from "./cache";

import {
  FullAssessmentData,
  PageAssessmentSummaryData,
  SectionAssessmentSummaryData,
  SectionEnrollmentData,
  SectionOutcomeSummaryData,
  SectionScoresData,
  StudentOutcomeCompetencyData,
} from "api/api-reporting-types";
import { getOrgIdFromCookie } from "utils/auth";

export async function serverGetPageAssessmentStatuses({
  pageIds,
  sectionId,
}: {
  pageIds?: string[];
  sectionId?: string;
}): Promise<PageAssessmentStatusType> {
  const params: { page_ids?: string[]; section_id?: string } = {};
  if (pageIds) {
    params.page_ids = pageIds;
  }

  if (sectionId) {
    params.section_id = sectionId;
  }

  try {
    const response = await serverApi.post<PageAssessmentStatusType>(
      `${SERVER_API_PATH}/material/pages/assessment_status/`,
      params,
    );

    return response.data;
  } catch (error) {
    reportApiError("serverPageAssessmentStatus", error);
    throw error;
  }
}

// Type for the server response format
type UserInviteStatusResponseType = Array<{
  user_id: string;
  invite_status: string;
}>;

export async function serverGetUserInviteStatuses(): Promise<UserInviteStatusType> {
  try {
    const response = await serverApi.get<UserInviteStatusResponseType>(
      `${SERVER_API_PATH}/orgs/org_users/invite_status/`,
    );

    // Convert array of objects to Record<string, InviteStatus>
    const statusesRecord: UserInviteStatusType = {};
    response.data.forEach((item) => {
      statusesRecord[item.user_id] = item.invite_status as InviteStatus;
    });

    return statusesRecord;
  } catch (error) {
    reportApiError("serverUserInviteStatus", error);
    throw error;
  }
}

export async function serverGetSectionAssessmentSummary(
  params: SectionAssessmentSummaryParams,
): Promise<SectionAssessmentSummaryData[]> {
  try {
    const response = await serverApi.post<SectionAssessmentSummaryData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/section_summary/`,
      params,
    );

    return response.data;
  } catch (error) {
    reportApiError("serverGetSectionAssessmentSummary", error);
    throw error;
  }
}

export async function serverGetPageAssessmentSummary(
  params: SectionAssessmentSummaryParams,
): Promise<PageAssessmentSummaryData[]> {
  try {
    const response = await serverApi.post<PageAssessmentSummaryData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/page_summary/`,
      params,
    );

    return response.data;
  } catch (error) {
    reportApiError("serverGetPageAssessmentSummary", error);
    throw error;
  }
}

export async function serverGetFullAssessmentData(
  params: SectionAssessmentSummaryParams,
): Promise<FullAssessmentData[]> {
  try {
    const response = await serverApi.post<FullAssessmentData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/full_report/`,
      params,
    );

    return response.data;
  } catch (error) {
    reportApiError("serverGetFullAssessmentData", error);
    throw error;
  }
}

export async function serverGetSectionEnrollmentSummary(params: {
  term_id: string;
  course_id?: string;
}): Promise<SectionEnrollmentData[]> {
  try {
    const response = await serverApi.post<SectionEnrollmentData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/section_enrollment_summary/`,
      params,
    );

    return response.data;
  } catch (error) {
    reportApiError("serverGetSectionEnrollmentSummary", error);
    throw error;
  }
}

export async function serverGetSectionOutcomeSummary(
  termId: string,
): Promise<SectionOutcomeSummaryData[]> {
  try {
    const response = await serverApi.post<SectionOutcomeSummaryData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/section_outcome_summary/`,
      { term_id: termId },
    );

    return response.data;
  } catch (error) {
    reportApiError("serverGetSectionOutcomeSummary", error);
    throw error;
  }
}

export async function serverGetStudentOutcomeCompetency(
  termId?: string,
): Promise<StudentOutcomeCompetencyData[]> {
  try {
    const response = await serverApi.post<StudentOutcomeCompetencyData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/student_outcome_competency/`,
      { term_id: termId },
    );

    return response.data;
  } catch (error) {
    reportApiError("serverGetStudentOutcomeCompetency", error);
    throw error;
  }
}

export async function serverGetSectionScores(
  termId: string,
  courseId: string | undefined,
): Promise<SectionScoresData[]> {
  try {
    const response = await serverApi.post<SectionScoresData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/section_scores/`,
      { term_id: termId, course_id: courseId },
    );

    return response.data;
  } catch (error) {
    reportApiError("serverGetSectionScores", error);
    throw error;
  }
}

// Returns engagement data for all 3 date ranges: 7 days, 30 days, and -1 (all time)
export const serverGetEngagementData = createCached(
  async (sectionId: string): Promise<Record<number, Record<string, UserEngagementDataType>>> => {
    try {
      const response = await serverApi.post<Record<number, Record<string, UserEngagementDataType>>>(
        `${SERVER_API_PATH}/material/sections/${sectionId}/engagement/`,
        {},
      );

      return response.data;
    } catch (error) {
      reportApiError("serverGetEngagementData", error);
      throw error;
    }
  },
  60 * 10, // 10 minutes
);

export async function serverGetServerTime(): Promise<{ server_time: number }> {
  try {
    const response = await serverApi.get<{ server_time: number }>("/api/v1/basebox/server-time/", {
      headers: {
        "Cache-Control": "no-store",
        Pragma: "no-cache",
      },
    });
    return response.data;
  } catch (error) {
    reportApiError("serverGetServerTime", error);
    throw error;
  }
}

export async function serverGetProgressOverviewData(): Promise<ProgressOverviewData> {
  try {
    const response = await serverApi.get<ProgressOverviewData>(
      `${SERVER_API_PATH}/material/outcome_assessments/progress_overview/`,
    );

    return response.data;
  } catch (error) {
    reportApiError("serverGetProgressOverviewData", error);
    throw error;
  }
}

// This should only be used for the monitoring dashboard. It retrieves nested section data across all orgs.
export async function serverGetSectionsForMonitoring(): Promise<SectionMonitoringData[]> {
  try {
    const response = await serverApi.get<SectionMonitoringData[]>(
      `${SERVER_API_PATH}/material/sections/for_monitoring/`,
    );
    return response.data;
  } catch (error) {
    reportApiError("serverGetSectionsForMonitoring", error);
    throw error;
  }
}

export async function serverGetInstructorAssessments() {
  const orgId = getOrgIdFromCookie();
  try {
    const response = await serverApi.get<InstructorAssessmentDataForSectionType[]>(
      `${SERVER_API_PATH}/orgs/orgs/${orgId}/instructor_assessments/`,
    );
    return response.data;
  } catch (error) {
    reportApiError("serverGetInstructorAssessments", error);
    throw error;
  }
}

export async function serverGetStudentAssessments() {
  const orgId = getOrgIdFromCookie();
  try {
    const response = await serverApi.get<StudentAssessmentDataForSectionType[]>(
      `${SERVER_API_PATH}/orgs/orgs/${orgId}/student_assessments/`,
    );
    return response.data;
  } catch (error) {
    reportApiError("serverGetStudentAssessments", error);
    throw error;
  }
}

export async function serverGetHomePlannerPages(userRole: CoreUserRoleType): Promise<PageType[]> {
  try {
    const response = await serverApi.get<PageType[]>(
      `${SERVER_API_PATH}/material/pages/home_planner/?user_role=${userRole}`,
    );

    return response.data;
  } catch (error) {
    reportApiError("serverGetHomePlannerPages", error);
    throw error;
  }
}

export async function serverGetSystemCheckResults(
  start_date: string,
): Promise<SystemCheckResultsData[]> {
  try {
    const response = await serverApi.get(`${SERVER_API_PATH}/system_check/check_results/`, {
      params: { created_at__gte: start_date },
    });
    return response.data;
  } catch (error) {
    reportApiError("serverGetSystemCheckResults", error);
    throw error;
  }
}
